import { observable } from 'mobx';
import {
  LayoutEnum,
  partListLabelMap,
  TopList,
  TypeEnum,
  PAGE_TYPE,
  BROKEN_TYPE,
  UPLOAD_IMG_SCENE,
} from '@/pages/fault-report/const';
import { UrlParams } from '@/pages/fault-report';
import {
  TypeListItem,
  BikeInfoResponse,
  FaultSubmitResponse,
  FaultSubmitRequest,
  PopWindowsItem,
} from '@/interface/services/faultReport';
import Toast from '@/components/toast';
import Api from '@/services/faultReport';
import { alipayCallNative } from '@hb/bridge-core';
import TIANQI from '@/pages/fault-report/tianqi';

export interface CheckItemPart extends TypeListItem {
  label?: string;
  key?: string;
  uniqueness: string;
  reverse: boolean;
  value: boolean;
  disable: boolean;
  className?: string;
  layoutEnum: number;
  uploadImg: boolean;
}

export default class FaultReport {
  @observable public urlParams: UrlParams = {
    scene: PAGE_TYPE.BEFORE_RIDE,
  };

  @observable public damageParts: Array<CheckItemPart> = [];

  @observable public damageBottomParts: Array<CheckItemPart> = [];

  @observable public hasCheckOther = false; // 是否勾选其他

  @observable public lackPhoto = true; // 缺少照片

  @observable public lackDesc = true; // 缺少描述

  @observable public uploadImg = false; // 是否必传图片（首页入口必传，其他入口根据所勾选故障判断）

  @observable public localPhotos: Array<string> = [];

  @observable public faultDesc = '';

  @observable public showReportRes = false;

  @observable public popWindows: PopWindowsItem = {};

  @observable public isfFirstProblem = 'Y';

  public isLoading = false;

  public setUrlParams = (params: UrlParams) => {
    this.urlParams = { ...this.urlParams, ...params };
  };

  public formatToClassName = (raw: string) =>
    raw.replace(/([A-Z])/g, (item) => `-${item.toLowerCase()}`);

  public initDamageParts = (raw: Array<TypeListItem>) => {
    const topItems: Array<CheckItemPart> = [];
    const bottomItems: Array<CheckItemPart> = [];

    raw.forEach((item, index) => {
      if (index < TopList.length) {
        const k = TopList[index];
        const itemConfig = partListLabelMap[k];
        topItems.push({
          ...itemConfig,
          ...item,
          value:
            !!item.status ||
            item.category === Number(this.urlParams.checkCategory),
          disable: !!item.status,
          className: this.formatToClassName(itemConfig.key),
          layoutEnum: LayoutEnum.top,
          uploadImg: item.scene === UPLOAD_IMG_SCENE.UPLOAD_SCENE,
        });
      } else {
        let value =
          !!item.status ||
          item.category === Number(this.urlParams.checkCategory);
        if (item.category === +TypeEnum.other) {
          value = false;
        }
        bottomItems.push({
          ...item,
          uniqueness: item.category.toString(),
          value,
          disable: !!item.status,
          reverse: false,
          layoutEnum: LayoutEnum.bottom,
          uploadImg: item.scene === UPLOAD_IMG_SCENE.UPLOAD_SCENE,
        });
      }
    });

    this.damageParts = topItems;
    this.damageBottomParts = bottomItems;

    const all = [...topItems, ...bottomItems];
    const isfFirst = !all.some(({ disable }) => disable);
    this.isfFirstProblem = isfFirst ? 'Y' : 'N';
    const { scene, rideGuid } = this.urlParams;
    TIANQI.pageView(this.isfFirstProblem, {
      pageEntry: scene,
      order_id: rideGuid,
    });
  };

  public handleItemChecked = (nextVal: boolean, clickedItem: CheckItemPart) => {
    const { layoutEnum } = clickedItem;
    const { uniqueness } = clickedItem;

    const update = (list: Array<CheckItemPart> = []) =>
      list.map((item) => ({
        ...item,
        value: item.uniqueness === uniqueness ? nextVal : item.value,
      }));

    if (layoutEnum === LayoutEnum.top) {
      this.damageParts = update(this.damageParts);
    } else if (layoutEnum === LayoutEnum.bottom) {
      this.damageBottomParts = update(this.damageBottomParts);
    }

    this.judgeAvailability();
  };

  public handleLocalPhotosChanged = (newVal: string) => {
    if (newVal) {
      this.localPhotos.push(newVal);
    }

    this.judgeAvailability();
  };

  public getLackPhoto = (uploadImg: boolean) => {
    // 必须上传至少一张图
    const least =
      this.localPhotos.filter((path) => path !== undefined).length === 0;
    return least && uploadImg;
  };

  public judgeAvailability = () => {
    // 如果选中的是其他-需要有补充说明
    let hasCheckOther = false;
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < this.damageBottomParts.length; index++) {
      const { uniqueness, value } = this.damageBottomParts[index];
      if (uniqueness === TypeEnum.other && value === true) {
        hasCheckOther = true;
        break;
      }
    }

    const uploadImg = this.damageParts.find(
      (e) => e.value && e.uploadImg && !e.disable,
    );

    this.lackDesc = hasCheckOther && !this.faultDesc; // 选项包含“其他”时，必须有文字描述
    this.lackPhoto = this.getLackPhoto(!!uploadImg);
    this.uploadImg = !!uploadImg;
  };

  public validForm = (bikeNo: string) => {
    const allData = [...this.damageParts, ...this.damageBottomParts];

    if (bikeNo === undefined || bikeNo === '' || bikeNo === null) {
      return '请输入车辆编号';
    }
    if (!/^\d{10}$/.test(bikeNo)) {
      return '编号位数错误';
    }
    if (!allData.some(({ value, disable }) => value && !disable)) {
      return '请选择故障部位';
    }
    if (this.lackPhoto) {
      return '请上传图片';
    }
    if (this.lackDesc) {
      return '请对其他故障进行补充描述';
    }
    return undefined;
  };

  public submitFault = async (inputBikeNo: string) => {
    this.judgeAvailability();
    const bikeNo = inputBikeNo || this.urlParams.bikeNo || '';
    const toastMessage = this.validForm(bikeNo);
    if (toastMessage) {
      Toast.showError(toastMessage);
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      const res: BikeInfoResponse = await Api.getBikeInfo({ bikeNo });
      const { status } = res || {};

      if (!status) {
        Toast.showError('编号输入错误');
        this.isLoading = false;
        return;
      }
    } catch (e) {
      this.isLoading = false;
    }

    await this.handleReport(bikeNo);
    this.isLoading = false;
  };

  public handleReport = async (bikeNo: string) => {
    const { rideGuid, scene, lng, lat } = this.urlParams;
    const params: FaultSubmitRequest = {
      rideGuid,
      scene,
      lng,
      lat,
      bikeNo,
      remark: this.faultDesc,
      images: this.localPhotos,
      fastReport: 0,
      faultCodes: [],
      categorys: [],
    };

    const allData = [...this.damageParts, ...this.damageBottomParts];

    const selected = allData
      .map((item, index) => {
        const isOther = index === 9;
        return {
          ...item,
          type: isOther ? BROKEN_TYPE.OTHERS : BROKEN_TYPE.BIKE_BREAKDOWN,
        };
      })
      .filter(({ value, disable }) => value && !disable);
    const faultCodes: Array<number> = [];
    const categorys: Array<number> = [];
    selected.forEach(({ typeCode, category }) => {
      faultCodes.push(typeCode);
      categorys.push(category);
    });

    params.faultCodes = faultCodes;
    params.categorys = categorys;

    try {
      const res: FaultSubmitResponse = await Api.faultSubmit(params);
      this.handleSubmitResult(res);
    } catch (e) {
      Toast.showError(e);
    } finally {
      TIANQI.clickButton(this.isfFirstProblem, {
        pageEntry: scene,
        order_id: rideGuid,
      });
    }
  };

  public handleSubmitResult = (res: FaultSubmitResponse) => {
    if (res.popWindows) {
      this.showReportRes = true;
      this.popWindows = res.popWindows;
    } else {
      Toast.show(res.hint);
      setTimeout(() => {
        alipayCallNative({
          classMap: 'Native',
          command: 'navigateBack',
        });
      }, 3000);
    }
  };
}
